import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import { CalendarClock, Send, Clock, X, Repeat, CalendarDays } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Right-sidebar Scheduler panel for students.
 * Flow: pick vertical → pick type (long_term | one_time) → pick slot(s) from
 * teacher's availability → review → submit.
 */
export default function StudentSchedulerPanel({ studentVerticals = [] }) {
  const [type, setType] = useState('long_term');
  const [vertical, setVertical] = useState('');
  const [teacherData, setTeacherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState([]);      // array of slot dicts
  const [review, setReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myReqs, setMyReqs] = useState([]);

  // Only verticals the student is actively enrolled in
  const verticalOptions = useMemo(
    () => (studentVerticals || [])
      .filter(v => (typeof v === 'object' ? (v.status || 'Active') === 'Active' : true))
      .map(v => (typeof v === 'object' ? v.vertical : v))
      .filter(Boolean),
    [studentVerticals]
  );

  // Initialize vertical dropdown once options arrive
  useEffect(() => {
    if (!vertical && verticalOptions.length > 0) setVertical(verticalOptions[0]);
  }, [verticalOptions, vertical]);

  // Load teacher availability for the selected vertical
  const loadAvailability = useCallback(async () => {
    if (!vertical) return;
    setLoading(true);
    try {
      // Ask backend which teacher is mapped and their slots via a helper endpoint:
      // We create a fake request preview by calling requests/mine (to learn our
      // teacher) OR we can enrich via /scheduler/availability/{teacher_id}.
      // Simpler: use the "verticals_breakdown" on parent hub, or embed teacher_id
      // in the props. For now, resolve via `/scheduler/lookup` — piggyback via
      // create dry-run... but easier: call our /scheduler/availability with the
      // teacher_id from studentVerticals row.
      // Always resolve via backend — client-side teacher_id in verticals[]
      // is the sheet ID (e.g. TID-006), not the auth user id.
      const res = await api.get('/scheduler/lookup-teacher', { params: { vertical } });
      const rtid = res.data?.teacher_user_id;
      if (rtid) {
        const av = await api.get(`/scheduler/availability/${rtid}`);
        setTeacherData(av.data);
      } else {
        setTeacherData(null);
      }
    } catch (_e) { setTeacherData(null); }
    finally { setLoading(false); }
  }, [vertical]);

  useEffect(() => { setPicked([]); loadAvailability(); }, [loadAvailability]);

  const loadMyReqs = useCallback(async () => {
    try {
      const res = await api.get('/scheduler/requests/mine');
      setMyReqs(res.data.requests || []);
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { loadMyReqs(); const t = setInterval(loadMyReqs, 30000); return () => clearInterval(t); }, [loadMyReqs]);

  const kind = type === 'long_term' ? 'recurring' : 'onetime';
  const availableSlots = useMemo(
    () => (teacherData?.slots || []).filter(s => s.kind === kind && !s.reserved),
    [teacherData, kind]
  );

  const toggleSlot = (slot) => {
    const key = slot.kind === 'recurring' ? `r:${slot.day_of_week}:${slot.start_time}` : `o:${slot.date}:${slot.start_time}`;
    const already = picked.some(p => (p.kind === 'recurring' ? `r:${p.day_of_week}:${p.start_time}` : `o:${p.date}:${p.start_time}`) === key);
    if (already) {
      setPicked(picked.filter(p => (p.kind === 'recurring' ? `r:${p.day_of_week}:${p.start_time}` : `o:${p.date}:${p.start_time}`) !== key));
    } else {
      setPicked([...picked, slot]);
    }
  };

  const submit = async () => {
    if (picked.length === 0) { toast.error('Pick at least one slot'); return; }
    setSubmitting(true);
    try {
      await api.post('/scheduler/requests', {
        vertical, schedule_type: type,
        slots: picked.map(p => p.kind === 'recurring'
          ? { day_of_week: p.day_of_week, start_time: p.start_time }
          : { date: p.date, start_time: p.start_time }),
      });
      toast.success('Request sent for teacher approval');
      setPicked([]); setReview(false);
      await loadMyReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const cancelReq = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try {
      await api.post(`/scheduler/requests/${id}/cancel`);
      toast.success('Cancelled');
      await loadMyReqs();
      await loadAvailability();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <aside className="nb-card p-4 space-y-3" data-testid="student-scheduler-panel">
      <div className="flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-indigo-600" />
        <h3 className="font-bold text-[#0F172A] text-sm">Scheduler</h3>
      </div>

      {verticalOptions.length === 0 ? (
        <p className="text-xs text-slate-400">No active verticals yet.</p>
      ) : review ? (
        <div className="space-y-3" data-testid="scheduler-review">
          <p className="text-xs font-bold text-slate-600">Review your request</p>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs space-y-1">
            <p><b>Vertical:</b> {vertical}</p>
            <p><b>Type:</b> {type === 'long_term' ? 'Long Term (recurring weekly)' : 'One Time'}</p>
            <p><b>Teacher:</b> {teacherData?.teacher_name} <span className="text-slate-500">({teacherData?.teacher_tz})</span></p>
            <div>
              <b>Slots:</b>
              <ul className="mt-1 space-y-0.5">
                {picked.map((s, i) => (
                  <li key={i} className="text-slate-800">• {s.kind === 'recurring' ? `Every ${DAYS[s.day_of_week]}` : s.date} at <b>{s.start_time}</b> (60 min)</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 flex-1 text-xs" data-testid="submit-req-btn">
              <Send className="w-3 h-3 mr-1" /> {submitting ? 'Sending…' : 'Send Request'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReview(false)} className="text-xs">Back</Button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Vertical</label>
            <Select value={vertical} onValueChange={setVertical}>
              <SelectTrigger className="h-8 text-xs" data-testid="sched-vertical"><SelectValue /></SelectTrigger>
              <SelectContent>{verticalOptions.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Type</label>
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setType('long_term')}
                className={`text-xs font-bold py-1.5 rounded-md border ${type === 'long_term' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
                data-testid="sched-long-term"
              ><Repeat className="w-3 h-3 inline mr-1" /> Long Term</button>
              <button
                type="button"
                onClick={() => setType('one_time')}
                className={`text-xs font-bold py-1.5 rounded-md border ${type === 'one_time' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
                data-testid="sched-one-time"
              ><CalendarDays className="w-3 h-3 inline mr-1" /> One Time</button>
            </div>
          </div>
          {loading ? (
            <p className="text-xs text-slate-400 py-2">Loading availability…</p>
          ) : !teacherData ? (
            <p className="text-xs text-slate-400 py-2">No teacher mapped yet for this vertical.</p>
          ) : (
            <>
              <p className="text-[11px] text-slate-500">
                Teacher: <b>{teacherData.teacher_name}</b> · TZ <b>{teacherData.teacher_tz}</b>
                {teacherData.intro_video_file_id && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/uploads/${teacherData.intro_video_file_id}?auth=${localStorage.getItem('token')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-2 text-indigo-600 hover:text-indigo-800 font-bold"
                    data-testid="watch-intro-video"
                  >▶ Watch Intro</a>
                )}
              </p>
              <div className="max-h-52 overflow-y-auto space-y-1">
                {availableSlots.length === 0 && (
                  <p className="text-xs text-slate-400 py-2">No open {type === 'long_term' ? 'recurring' : 'one-time'} slots available.</p>
                )}
                {availableSlots.map(s => {
                  const key = s.kind === 'recurring' ? `r:${s.day_of_week}:${s.start_time}` : `o:${s.date}:${s.start_time}`;
                  const isPicked = picked.some(p => (p.kind === 'recurring' ? `r:${p.day_of_week}:${p.start_time}` : `o:${p.date}:${p.start_time}`) === key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleSlot(s)}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-xs border flex items-center justify-between ${isPicked ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'}`}
                      data-testid={`slot-${key}`}
                    >
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {s.kind === 'recurring' ? `Every ${DAYS[s.day_of_week]}` : s.date}
                      </span>
                      <span className="font-bold">{s.start_time}</span>
                    </button>
                  );
                })}
              </div>
              {picked.length > 0 && (
                <Button size="sm" onClick={() => setReview(true)} className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs" data-testid="review-req-btn">
                  Review ({picked.length}) →
                </Button>
              )}
            </>
          )}
        </>
      )}

      {/* My existing requests — pending (esp. with suggestions) always shown first */}
      {myReqs.length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <p className="text-[10px] font-bold text-slate-500 uppercase mb-1.5">My Requests</p>
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {(() => {
              // Guarantee pending rows (especially those with teacher suggestions) always render.
              // Sort: pending first, then approved, then everything else, and prioritise items with suggested_slots.
              const rank = (r) => {
                if (r.status === 'pending' && (r.suggested_slots || []).length > 0) return 0;
                if (r.status === 'pending') return 1;
                if (r.status === 'approved') return 2;
                return 3;
              };
              const sorted = [...myReqs].sort((a, b) => rank(a) - rank(b));
              return sorted.slice(0, 8);
            })().map(r => (
              <div key={r.id} className="text-xs bg-slate-50 rounded-md px-2 py-1.5" data-testid={`my-req-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800 truncate">{r.vertical} · {r.schedule_type.replace('_', ' ')}</p>
                    <p className="text-[10px] text-slate-500">{(r.slots || []).length} slot(s)</p>
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : r.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {r.status.toUpperCase()}
                  </span>
                  {(r.status === 'pending' || r.status === 'approved') && (
                    <button onClick={() => cancelReq(r.id)} className="ml-1 text-slate-400 hover:text-red-500" title="Cancel"><X className="w-3 h-3" /></button>
                  )}
                </div>
                {/* Counter-proposal inline accept */}
                {r.status === 'pending' && r.suggested_slots && r.suggested_slots.length > 0 && (
                  <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200" data-testid={`suggest-${r.id}`}>
                    <p className="text-[10px] font-bold text-amber-800 mb-1">Teacher suggested alternative slots:</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {r.suggested_slots.map((s, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-amber-300 text-amber-800 font-bold">
                          {s.day_of_week != null ? DAYS[s.day_of_week] : s.date} · {s.start_time}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      <Button size="sm" onClick={async () => {
                        // Accept: POST new request first, then cancel old only on success.
                        // Reversed order avoids the data-loss window where old is cancelled
                        // but new POST fails (e.g. "Slot just taken" 409).
                        try {
                          await api.post('/scheduler/requests', {
                            vertical: r.vertical, schedule_type: r.schedule_type,
                            slots: r.suggested_slots,
                          });
                          try { await api.post(`/scheduler/requests/${r.id}/cancel`); } catch (_) { /* old cancel best-effort */ }
                          toast.success('Accepted · Sent updated request');
                          await loadMyReqs();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                      }} className="h-6 text-[10px] px-2 bg-amber-600 hover:bg-amber-700" data-testid={`accept-suggest-${r.id}`}>
                        Accept
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => cancelReq(r.id)} className="h-6 text-[10px] px-2" data-testid={`decline-suggest-${r.id}`}>
                        Decline
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
